import React, { useState, useEffect } from 'react';
import { useAuth } from '../../shared/hooks';
import { Button, Input, Card } from '../../shared/components';

const ResetPasswordScreen: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [loading, setLoading] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const { verifyPasswordResetCode, confirmPasswordReset } = useAuth();

  useEffect(() => {
    // Extraer el código de la URL usando window.location
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('oobCode');
    if (code) {
      setOobCode(code);
      verifyCode(code);
    } else {
      setMessage('Enlace inválido o expirado. Por favor solicita un nuevo enlace de recuperación.');
      setMessageType('error');
    }
  }, []);

  const verifyCode = async (code: string) => {
    setLoading(true);
    const result = await verifyPasswordResetCode(code);
    setLoading(false);
    
    if (result.success) {
      setCodeVerified(true);
      setMessage('Código verificado. Por favor ingresa tu nueva contraseña.');
      setMessageType('success');
    } else {
      setMessage(result.error || 'Enlace inválido o expirado.');
      setMessageType('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    
    if (!newPassword || !confirmPassword) {
      setMessage('Por favor completa todos los campos');
      setMessageType('error');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('La contraseña debe tener al menos 6 caracteres');
      setMessageType('error');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Las contraseñas no coinciden. Por favor inténtalo de nuevo.');
      setMessageType('error');
      return;
    }

    if (!oobCode) {
      setMessage('Error: Código de verificación no encontrado');
      setMessageType('error');
      return;
    }

    setLoading(true);
    const result = await confirmPasswordReset(oobCode, newPassword);
    setLoading(false);
    
    if (result.success) {
      setMessage('Contraseña actualizada exitosamente. Redirigiendo al inicio de sesión...');
      setMessageType('success');
      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } else {
      setMessage(result.error || 'Error al actualizar la contraseña');
      setMessageType('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4 sm:p-6 md:p-8 flex items-center justify-center">
      <Card className="w-full max-w-md p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Restablecer Contraseña
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {codeVerified ? 'Ingresa tu nueva contraseña' : 'Verificando enlace...'}
          </p>
        </div>

        {codeVerified ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              label="Nueva contraseña"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />

            <Input
              type="password"
              label="Confirmar nueva contraseña"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />

            {message && (
              <div className={`px-4 py-3 rounded-lg text-sm ${
                messageType === 'success' ? 'bg-green-50 border border-green-200 text-green-600' :
                messageType === 'error' ? 'bg-red-50 border border-red-200 text-red-600' :
                'bg-blue-50 border border-blue-200 text-blue-600'
              }`}>
                {message}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
            </Button>
          </form>
        ) : (
          <div className="text-center">
            {message && (
              <div className={`px-4 py-3 rounded-lg text-sm ${
                messageType === 'success' ? 'bg-green-50 border border-green-200 text-green-600' :
                messageType === 'error' ? 'bg-red-50 border border-red-200 text-red-600' :
                'bg-blue-50 border border-blue-200 text-blue-600'
              }`}>
                {message}
              </div>
            )}
            {!message && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            )}
          </div>
        )}

        <div className="mt-6 text-center">
          <a
            href="/login"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Volver al inicio de sesión
          </a>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordScreen;
